import type { ClusterName } from '../constants'
import { CLUSTERS } from '../constants'
import { UnknownServerError } from '../errors'

// Turning what someone typed — `dev`, or a URL — into a server address. Shared by every command that takes
// a server: `use`, and the `--server` option on `init` and `login`.

export const CLUSTER_NAMES = Object.keys(CLUSTERS) as ClusterName[]

/** Trailing slashes are noise: the same server typed two ways must compare equal. */
export function normalizeServerUrl(serverUrl: string): string {
  return serverUrl.replace(/\/+$/, '')
}

/** Which cluster a URL is, or null when it is not one of ours. */
export function clusterFor(serverUrl: string): ClusterName | null {
  const normalized = normalizeServerUrl(serverUrl)

  return CLUSTER_NAMES.find(name => CLUSTERS[name] === normalized) ?? null
}

/**
 * Resolve `prod` / `dev` / `local`, or an explicit URL, to a server URL.
 *
 * Anything that is not a known name and does not look like a URL is refused rather than passed through: a
 * mistyped `prd` would otherwise be stored as a server address and fail much later, against a host that
 * does not exist.
 */
export function resolveServerTarget(target: string): string {
  const known = CLUSTERS[target as ClusterName]

  if (known) {
    return known
  }

  if (/^https?:\/\//i.test(target)) {
    return normalizeServerUrl(target)
  }

  throw new UnknownServerError(target, [...CLUSTER_NAMES, 'or a full http(s):// URL'])
}
